import { ChangeEvent, FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import { useAbility } from '@casl/react';

import PlusIcon from '../../../assets/icons/plus.svg';
import { ReactComponent as RedTrashIcon } from '../../../assets/icons/red-trash.svg';
import RedTrashImage from '../../../assets/icons/red-trash.svg';
import EditIconImage from '../../../assets/icons/edit.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as ChevronUpIcon } from '../../../assets/icons/chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/chevron-down.svg';
// import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import {
  useGetTeamMembersQuery,
  useDeleteTeamMembersMutation,
  useResendVerificationEmailMutation,
  displayNotification,
} from '../../../services';
import {
  // FilterDrawer,
  CenteredSpinner,
  RoundedBadge,
  Paginator,
  TableHeader,
  UserData,
} from '../../../components';
import { AbilityContext, Can } from '../../../providers';
import { useTypedSelector } from '../../../hooks';
// import { userFilters } from '../../../utils';
import { TeamMemberStatus } from '../../../models';
import { useForm, useFieldArray } from 'react-hook-form';
import { DeleteUsersModal, UpdateUsersModal } from '../components';
import { UserMutation } from '../models';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const Users: FC = () => {
  const ability = useAbility(AbilityContext);
  const { status } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);

  // const [filters, setFilters] = useState<{ status?: Status }>({
  //   status: undefined,
  // });
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  // const { isOpen, onOpen, onClose } = useDisclosure();

  const navigate = useNavigate();

  const { userDetails } = useTypedSelector(state => state.auth);

  const {
    data,
    isLoading,
    isFetching,
    // , refetch
  } = useGetTeamMembersQuery(
    {
      page,
      name: debouncedSearchTerm,
      companyId: Number(userDetails?.companyBasicInfo?.id),
      // ...filters,
      status: status !== 'all' ? (status as CompanyStatus) : undefined,
    },
    {
      skip: !userDetails?.companyBasicInfo?.id,
    }
  );

  const [deleteTeamMembers] = useDeleteTeamMembersMutation();
  const [resendVerificationEmail] = useResendVerificationEmailMutation();

  const { control, getValues } = useForm<{
    users: UserMutation[];
  }>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users',
  });

  const {
    isOpen: isDeleteUsersModalOpen,
    onOpen: onOpenDeleteUsersModal,
    onClose: onCloseDeleteUsersModal,
  } = useDisclosure();

  const {
    isOpen: isUpdateUsersModalOpen,
    onOpen: onOpenUpdateUsersModal,
    onClose: onCloseUpdateUsersModal,
  } = useDisclosure();

  // const handleFilterDrawerClose = (filterValues?: { status: Status }) => {
  //   onClose();
  //   if (!filterValues) return;

  //   setFilters({ status: filterValues.status });

  //   void refetch();
  // };

  // const handleRemoveFilter = (filterKey: keyof typeof filters) => {
  //   setFilters({ ...filters, [filterKey]: undefined });

  //   void refetch();
  // };

  const handelToggleCheckedUsers = (
    e: ChangeEvent<HTMLInputElement>,
    user: UserMutation
  ) => {
    if (e.target.checked) append(user);
    else {
      const userIndex = getValues('users').findIndex(
        ({ id }) => id === user.id
      );

      if (userIndex !== -1) remove(userIndex);
    }
  };

  const handleDeleteUsers = async (users: UserMutation[]) => {
    // The trigger is NOT unwrapped, so a body-level failure resolves normally.
    // Classify it before claiming anything happened: the old code toasted
    // "you have successfully deleted N users!" for a rejected delete too.
    const response = await deleteTeamMembers(users.map(({ id }) => ({ id })));

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not delete the selected users',
        description:
          'No user was deleted. Please try again or contact BrickLynk support.',
      });
      return;
    }

    displayNotification({
      title: `you have successfully deleted ${users.length} users!`,
      description: `Your have deleted (${users
        .map(({ name }, index) => {
          if (users.length === 1 || users.length === index + 1) return name;
          // Was a bare expression with no `return`, so every name except the
          // last resolved to undefined and vanished in the join: a 3-user
          // delete listed one name. Same defect class as this ticket.
          else return ` ${name} -`;
        })
        .join('')}).`,
    });
  };

  const handleResendVerificationEmail = async (id: number) => {
    // Was fire-and-forget on an invite path: the click did nothing visible
    // whether the mail went out or not.
    const response = await resendVerificationEmail({ id });

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not resend the invitation',
        description:
          'The invitation email was not sent. Please try again in a moment.',
      });
      return;
    }

    displayNotification({
      title: 'Invitation sent',
      description: 'The invitation email has been resent to this user.',
      status: 'success',
    });
  };

  return (
    <Box h="full">
      {/* {isOpen && (
        <FilterDrawer
          isOpen={isOpen}
          onClose={handleFilterDrawerClose}
          filters={userFilters}
        />
      )} */}

      {isDeleteUsersModalOpen && (
        <DeleteUsersModal
          isOpen={isDeleteUsersModalOpen}
          onClose={isDeleted => {
            if (isDeleted) void handleDeleteUsers(getValues('users'));
            onCloseDeleteUsersModal();
          }}
        />
      )}

      {isUpdateUsersModalOpen && (
        <UpdateUsersModal
          isOpen={isUpdateUsersModalOpen}
          users={getValues('users')}
          onClose={onCloseUpdateUsersModal}
        />
      )}

      <VStack align="stretch">
        <HStack justify="space-between" mt="4" align="start">
          <HStack align="baseline">
            <Heading
              color="custom_blue.1"
              fontSize="2xl"
              textTransform="capitalize"
            >
              {status !== 'all' ? status : ''} Users
            </Heading>
            <Box color="custom_gray.1" fontSize="sm">
              (
              {(data ? String(data?.data.totalElements) : '0').padStart(2, '0')}{' '}
              Users)
            </Box>
          </HStack>

          <HStack>
            <Menu closeOnSelect={false}>
              {fields.length
                ? ({ isOpen }) => (
                    <>
                      <MenuButton
                        as={Button}
                        rightIcon={
                          isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />
                        }
                        fontSize="sm"
                        bg="none"
                        _hover={{ bg: 'none' }}
                        border="1px"
                        borderColor="custom_gray.1"
                      >
                        Actions
                      </MenuButton>
                      <MenuList p="0">
                        <Can I="PUT" a="teamMember">
                          <MenuItem
                            onClick={onOpenUpdateUsersModal}
                            border="1px"
                            borderColor="custom_gray.4"
                            p="3"
                          >
                            <HStack>
                              <Image src={EditIconImage} alt="Edit users" />
                              <Text
                                color="custom_blue.600"
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                Update
                              </Text>
                            </HStack>
                          </MenuItem>
                        </Can>

                        <MenuItem
                          onClick={onOpenDeleteUsersModal}
                          border="1px"
                          borderColor="custom_gray.4"
                          p="3"
                        >
                          <HStack>
                            <Image src={RedTrashImage} alt="Delete users" />
                            <Text
                              color="error.600"
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              Delete
                            </Text>
                          </HStack>
                        </MenuItem>
                      </MenuList>
                    </>
                  )
                : null}
            </Menu>
            <Can I="POST" a="teamMember">
              <Button
                onClick={() => navigate(`/users/${status || 'all'}/new-user`)}
                bg="custom_orange.2"
                _hover={{ bg: 'custom_orange.2' }}
                color="white"
                fontWeight="semibold"
                fontSize="sm"
              >
                <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
                Create New User
              </Button>
            </Can>
          </HStack>
        </HStack>

        <VStack
          align="stretch"
          bg="white"
          border="1px"
          borderColor="custom_gray.3"
          my="4"
          p="3"
          gap="3"
          borderRadius="md"
          minH="80vh"
        >
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            {/* <Button
            leftIcon={<FiltersIcon />}
            color="custom_blue.1"
            fontWeight="semibold"
            fontSize="sm"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            border="1px"
            borderColor="custom_blue.3"
            bg="none"
            _hover={{ bg: 'none' }}
            onClick={onOpen}
          >
            Filters
          </Button> */}
          </HStack>

          {/* {Object.entries(filters).map(([key, value]) => {
          if (value)
            return (
              <HStack key={key}>
                <RoundedBadge
                  withCloseButton
                  onClose={() =>
                    handleRemoveFilter(key as keyof typeof filters)
                  }
                >
                  {value}
                </RoundedBadge>
              </HStack>
            );
        })} */}

          {/* <FilterBadges onRemoveFilter={handleRemoveFilter} filters={filters} /> */}

          {isLoading || isFetching ? (
            <CenteredSpinner />
          ) : (
            <TableContainer
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              minH="70vh"
            >
              <Table variant="simple">
                <Thead bg="custom_gray.2">
                  <Tr>
                    <TableHeader title="User" />
                    <TableHeader title="Status" />
                    <TableHeader title="Email" />
                    <TableHeader title="Phone Number" />
                    <TableHeader title="Company" />
                    <TableHeader title="Actions" />
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.data.content.map(
                    ({
                      countryCode,
                      id,
                      status,
                      firstName,
                      lastName,
                      email,
                      phoneNumber,
                      profilePictureThumbnailURL,
                      role,
                      partner,
                    }) => (
                      <Tr key={id}>
                        <Td border="none">
                          <UserData
                            roleName={role.name}
                            firstName={firstName}
                            lastName={lastName}
                            hasCheckbox={true}
                            pictureUrl={profilePictureThumbnailURL}
                            onChangeCheckbox={e =>
                              handelToggleCheckedUsers(e, {
                                id,
                                name: firstName,
                              })
                            }
                          />
                        </Td>
                        <Td border="none">
                          <RoundedBadge colorSchema={status}>
                            {status === TeamMemberStatus.PENDING ? (
                              <Menu>
                                <MenuButton
                                  as={Button}
                                  rightIcon={
                                    ability.can(
                                      'POST',
                                      'teamMember/resendVerificationEmail'
                                    ) ? (
                                      <ChevronDownIcon />
                                    ) : undefined
                                  }
                                  bg="transparent"
                                  _hover={{ bg: 'transparent' }}
                                  fontSize="inherit"
                                  color="inherit"
                                  p="0"
                                  h="fit-content"
                                  w="fit-content"
                                >
                                  {status === TeamMemberStatus.PENDING
                                    ? 'Pending Activation'
                                    : status}
                                </MenuButton>

                                <Can
                                  I="POST"
                                  a="teamMember/resendVerificationEmail"
                                >
                                  <MenuList>
                                    <MenuItem
                                      color="custom_blue.1"
                                      fontSize="sm"
                                      fontWeight="medium"
                                      onClick={() =>
                                        void handleResendVerificationEmail(id)
                                      }
                                    >
                                      Resend Invitation
                                    </MenuItem>
                                  </MenuList>
                                </Can>
                              </Menu>
                            ) : (
                              status
                            )}
                          </RoundedBadge>
                        </Td>
                        <Td border="none">{email}</Td>

                        <Td letterSpacing="tighter" border="none">
                          + {countryCode} {phoneNumber}
                        </Td>

                        <Td letterSpacing="tighter" border="none">
                          {partner && partner.name ? partner.name : '-'}
                        </Td>

                        <Td color="custom_blue.1" fontSize="xs" border="none">
                          <HStack>
                            <IconButton
                              icon={<RedTrashIcon />}
                              aria-label="delete user"
                              bg="transparent"
                              onClick={() => {
                                append({
                                  id,
                                  name: firstName,
                                });
                                onOpenDeleteUsersModal();
                              }}
                            />
                            <Can I="PUT" a="teamMember">
                              <IconButton
                                icon={<EditIcon />}
                                aria-label="delete user"
                                bg="transparent"
                                onClick={() => navigate(String(id))}
                              />
                            </Can>
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  )}
                </Tbody>
              </Table>

              <Paginator
                pageCount={Number(data?.data.totalPages)}
                onPageChange={page => setPage(page)}
              />
            </TableContainer>
          )}
        </VStack>
      </VStack>
    </Box>
  );
};
